import { makeAutoObservable, runInAction } from 'mobx'
import axios, { type CancelTokenSource, CanceledError } from 'axios'
import { debounce } from 'lodash'
import { logger } from 'shared/lib'
import { TagsApi } from 'entities/Tags'
import { type Tag } from 'entities/Tags/model/Tag'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type ISearchDropdownStoreOptions } from 'widgets/SearchDropdown/store/types'

export class SearchDropdownTagsStore {
  private _data: Tag[] = []
  private _loading = false
  private _search = ''
  private _total = 0
  private _page = 1
  private _perPage = 10
  private _maxLimit: number | null = null
  private _disableClosing = false
  private _debounceFetch
  private _selectedItemsMapIds: Map<number, number> = new Map()
  private _options: ISearchDropdownStoreOptions | null = null
  private _cancelTokenSource: CancelTokenSource | null = null

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
    this._debounceFetch = debounce(this.init, 500)
  }

  get firstSelectedItemId() {
    const id = this._selectedItemsMapIds.entries().next().value?.[0]

    if (!id) return null

    return id
  }

  get selectedItemsIds() {
    return Array.from(this._selectedItemsMapIds.values())
  }

  get selectedItemsListIds() {
    return Array.from(this._selectedItemsMapIds.entries()).map((item) => item[1])
  }

  get hasSelectedItems() {
    return !!this.selectedItemsCount
  }

  get selectedItemsCount() {
    return this._selectedItemsMapIds.size
  }

  get hasItems() {
    return !!this._data.length
  }

  get itemsList() {
    return this._data
  }

  get itemsListIds() {
    return this._data.map((item) => item.id)
  }

  get first3Items() {
    return this._data.slice(0, 3)
  }

  get hasMore() {
    const loadedCount = this._page * this._perPage

    return loadedCount < this._total
  }

  initCancelTokenSource = () => {
    this._cancelTokenSource?.cancel()
    this._cancelTokenSource = axios.CancelToken.source()
  }

  resetCancelTokenSource = () => {
    this._cancelTokenSource?.cancel()
  }

  initOptions = (options: ISearchDropdownStoreOptions) => {
    this._options = options
  }

  isSelected = (id: number) => {
    return !!this._selectedItemsMapIds.get(id)
  }

  selectTypeItem = (item: Tag) => {
    this.toggleTypeItem(item)
    this._options?.resetSearch?.()
  }

  resetType = () => {
    this._selectedItemsMapIds.clear()
    this.reset()
  }

  toggleTypeItem = (item: Tag) => {
    const id = item.id

    if (this._options?.selectedMode) {
      if (this._selectedItemsMapIds.has(id)) {
        this._selectedItemsMapIds.delete(id)
      } else {
        this._selectedItemsMapIds.set(id, id)
      }
    }

    this._options?.toggleTypeItem?.(item)
  }

  setSelectedItemsIds = (ids: number[]) => {
    this._selectedItemsMapIds = new Map(ids.map((id) => [id, id]))
  }

  reset = () => {
    this._data = []
    this._loading = false
    this._search = ''
    this._total = 0
    this._page = 1
    this._cancelTokenSource?.cancel()
    this._debounceFetch.cancel()
  }

  setMaxLimit = (maxLimit: number | null) => {
    this._maxLimit = maxLimit
  }

  setDisableClosing = (disableClosing: boolean) => {
    this._disableClosing = disableClosing
  }

  handleSearch = (value: string) => {
    this._search = value
    this.resetCancelTokenSource()
    this._debounceFetch()
  }

  loadMore = async () => {
    try {
      runInAction(() => {
        this._loading = true
        this.initCancelTokenSource()
      })

      const { data } = await TagsApi.getTagsPerPage(
        {
          search: this._search,
          page: this._page + 1,
          length: this._perPage,
        },
        {
          cancelToken: this._cancelTokenSource?.token,
        }
      )

      const items = this._entities.tagsStore.addItems(data.data)

      runInAction(() => {
        this._data = [...this._data, ...items]
        this._total = data.total
        this._page += 1
      })
    } catch (e) {
      if (e instanceof CanceledError) return

      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  init = async () => {
    try {
      runInAction(() => {
        this._loading = true
        this._data = []
        this._page = 1
        this.initCancelTokenSource()
      })

      const { data } = await TagsApi.getTagsPerPage(
        {
          search: this._search,
          page: this._page,
          length: this._perPage,
          exclude: this.selectedItemsListIds,
        },
        {
          cancelToken: this._cancelTokenSource?.token,
        }
      )

      const items = this._entities.tagsStore.addItems(data.data)

      runInAction(() => {
        this._data = items
        this._total = data.total
      })
    } catch (e) {
      if (e instanceof CanceledError) return

      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  get selectedItemsMapIds() {
    return this._selectedItemsMapIds
  }

  get search() {
    return this._search
  }

  get loading() {
    return this._loading
  }

  get options() {
    return this._options
  }

  get total() {
    return this._total
  }

  get disableClosing() {
    return this._disableClosing
  }

  get maxLimit() {
    return this._maxLimit
  }
}
